import React, { useEffect, useMemo, useState } from 'react';
import DraggableWindow from './DraggableWindow';
import CustomSelect from '../CustomSelect';
import { cuponsService, lojasService } from '../../services/api';

function formatDateBr(value) {
  if (!value) return '-';
  const str = String(value).slice(0, 10);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return str;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-');
    return `${d}/${m}/${y}`;
  }
  return str;
}

function money(value) {
  const num = parseFloat(value || 0);
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function normalizeDateKey(value) {
  if (!value) return '';
  const str = String(value).trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split('/');
    return `${y}-${m}-${d}`;
  }
  return str;
}

function formatCpf(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length !== 11) return value || '-';
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

const FORMA_PAGAMENTO_LABELS = {
  1: 'Dinheiro',
  4: 'Cartão',
  5: 'Cartão',
  8: 'PIX',
  9: 'Convênio',
  10: 'Vale',
  11: 'Vale'
};

const FORMAS_PAGAMENTO_PADRAO = [
  'Dinheiro',
  'Debito',
  'Credito',
  'Pix',
  'APP',
  'POS',
  'Convenio',
  'Cart.Dig.',
  'Cartão Web',
  'Compre+Rap'
];

function normalizePaymentMethod(value) {
  return String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

function formatarPagamento(item) {
  if (!item) return '-';
  const formaRaw = String(item.FORMAPG || '').trim();
  const formaNumerica = /^\d+$/.test(formaRaw) ? parseInt(formaRaw, 10) : null;
  const formaDescricao = formaNumerica !== null
    ? (FORMA_PAGAMENTO_LABELS[formaNumerica] || `Forma ${formaRaw}`)
    : (formaRaw || '-');
  const partes = [formaDescricao];
  const bandeira = String(item.BANDEIRA || '').trim();
  if (bandeira) partes.push(bandeira);
  const parcelas = parseInt(item.PARCELAS, 10);
  if (Number.isFinite(parcelas) && parcelas > 1) partes.push(`${parcelas}x`);
  return partes.join(' - ');
}

const WindowCupons = ({ onClose, onMinimize, isMinimized, zIndex, onFocus, userData }) => {
  const hoje = useMemo(() => {
    const atual = new Date();
    const formatar = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    const inicio = new Date(atual);
    inicio.setDate(inicio.getDate() - 7);
    return { dataIni: formatar(inicio), dataFim: formatar(atual) };
  }, []);

  const [cupom, setCupom] = useState('');
  const [filial, setFilial] = useState(userData?.loja_id || 22);
  const [dataIni, setDataIni] = useState(hoje.dataIni);
  const [dataFim, setDataFim] = useState(hoje.dataFim);
  const [valorMin, setValorMin] = useState('');
  const [valorMax, setValorMax] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('');
  const [formasPagamentoDisponiveis, setFormasPagamentoDisponiveis] = useState(FORMAS_PAGAMENTO_PADRAO);
  const [lojas, setLojas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState([]);
  const [resumoFormaPagamento, setResumoFormaPagamento] = useState(null);
  const [produtosPorCupom, setProdutosPorCupom] = useState({});
  const [pagamentosPorCupom, setPagamentosPorCupom] = useState({});
  const [clientesPorCupom, setClientesPorCupom] = useState({});
  const [expandedCupom, setExpandedCupom] = useState({});

  useEffect(() => {
    const carregarLojas = async () => {
      try {
        const lojasResponse = await lojasService.getLojas();
        setLojas((lojasResponse || []).sort((a, b) => a.numero - b.numero));
      } catch (err) {
        console.error('Erro ao carregar lojas:', err);
      }
    };
    carregarLojas();
  }, []);

  const nomeLoja = (numero) => {
    const loja = lojas.find((item) => String(parseInt(item.numero, 10)) === String(parseInt(numero, 10)));
    if (!loja) return String(numero || '-').padStart(2, '0');
    return `${String(loja.numero).padStart(2, '0')} - ${loja.nome}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setLoading(true);
    setResultado([]);
    setResumoFormaPagamento(null);
    setProdutosPorCupom({});
    setPagamentosPorCupom({});
    setClientesPorCupom({});
    setExpandedCupom({});

    try {
      if (!String(cupom).trim() && (!dataIni || !dataFim)) {
        throw new Error('Informe o intervalo de datas.');
      }

      const rows = await cuponsService.listarCupons({
        cupom,
        filial,
        dataIni,
        dataFim,
        valorMin,
        valorMax
      });

      const rowsValidos = Array.isArray(rows) ? rows : [];
      let rowsExibidos = rowsValidos;

      if (rowsValidos.length > 0) {
        try {
          const pagamentos = await cuponsService.listarPagamentosCupons({
            cupons: rowsValidos.map((row) => ({ documento: row.CUPOM, loja: row.LOJA, data: row.DATA })),
            filial
          });

          const pagamentosValidos = Array.isArray(pagamentos) ? pagamentos : [];
          const agrupados = pagamentosValidos.reduce((acc, item) => {
            const dataChave = normalizeDateKey(item.DATA);
            const chave = `${item.LOJA || filial}|${dataChave}|${item.DOCUMENTO}`;
            if (!acc[chave]) acc[chave] = [];
            acc[chave].push(item);
            return acc;
          }, {});

          setPagamentosPorCupom(agrupados);

          const chavesDosCupons = new Set(rowsValidos.map(
            (row) => `${row.LOJA || filial}|${normalizeDateKey(row.DATA)}|${row.CUPOM}`
          ));
          const formasEncontradas = [...new Set(
            pagamentosValidos
              .filter((item) => chavesDosCupons.has(
                `${item.LOJA || filial}|${normalizeDateKey(item.DATA)}|${item.DOCUMENTO}`
              ))
              .map((item) => String(item.FORMAPG || '').trim())
              .filter(Boolean)
          )].sort((a, b) => a.localeCompare(b, 'pt-BR'));
          setFormasPagamentoDisponiveis((anteriores) => [...new Set([
            ...FORMAS_PAGAMENTO_PADRAO,
            ...anteriores,
            ...formasEncontradas
          ])]);

          if (formaPagamento) {
            const formaSelecionada = normalizePaymentMethod(formaPagamento);
            rowsExibidos = rowsValidos.filter((row) => {
              const chaveExata = `${row.LOJA || filial}|${normalizeDateKey(row.DATA)}|${row.CUPOM}`;
              const pagamentosDoCupom = agrupados[chaveExata] || [];
              return pagamentosDoCupom.some(
                (item) => normalizePaymentMethod(item.FORMAPG) === formaSelecionada
              );
            });

            const totalFiltrado = rowsExibidos.reduce((total, row) => {
              const chaveExata = `${row.LOJA || filial}|${normalizeDateKey(row.DATA)}|${row.CUPOM}`;
              const pagamentosDaForma = (agrupados[chaveExata] || []).filter(
                (item) => normalizePaymentMethod(item.FORMAPG) === formaSelecionada
              );
              return total + pagamentosDaForma.reduce(
                (subtotal, item) => subtotal + (parseFloat(item.VALOR) || 0),
                0
              );
            }, 0);

            setResumoFormaPagamento({
              forma: formaPagamento,
              total: totalFiltrado,
              quantidadeCupons: rowsExibidos.length
            });
          }
        } catch (paymentErr) {
          console.error('Erro ao buscar pagamentos dos cupons:', paymentErr);
          if (formaPagamento) {
            throw new Error('Não foi possível aplicar o filtro de forma de pagamento.');
          }
        }
      }

      setResultado(rowsExibidos);

      if (rowsExibidos.length === 1 && String(cupom).trim()) {
        const unico = rowsExibidos[0];
        if (unico?.CUPOM) {
          const chave = `${unico.LOJA}|${unico.DATA}|${unico.HORA}|${unico.CUPOM}`;
          setExpandedCupom({ [chave]: true });
          await carregarDetalhesCupom(unico);
        }
      }
    } catch (err) {
      console.error('Erro ao buscar cupons:', err);
      setErro(err?.message || 'Erro ao buscar cupons.');
    } finally {
      setLoading(false);
    }
  };

  const carregarDetalhesCupom = async (row) => {
    const chave = `${row.LOJA}|${row.DATA}|${row.HORA}|${row.CUPOM}`;
    const chaveCliente = `${row.LOJA}|${normalizeDateKey(row.DATA)}|${row.HORA || ''}|${row.CUPOM}`;
    if (produtosPorCupom[chave] && clientesPorCupom[chaveCliente]) return;

    try {
      const [produtos, cliente] = await Promise.all([
        produtosPorCupom[chave]
          ? Promise.resolve(produtosPorCupom[chave])
          : cuponsService.listarProdutosCupom({ cupom: row.CUPOM, filial: row.LOJA, data: row.DATA, hora: row.HORA }),
        clientesPorCupom[chaveCliente]
          ? Promise.resolve(clientesPorCupom[chaveCliente])
          : cuponsService.listarClienteCupom({ cupom: row.CUPOM, filial: row.LOJA, data: row.DATA, hora: row.HORA })
      ]);
      setProdutosPorCupom((prev) => ({ ...prev, [chave]: Array.isArray(produtos) ? produtos : [] }));
      if (cliente) {
        setClientesPorCupom((prev) => ({ ...prev, [chaveCliente]: cliente }));
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes do cupom:', err);
      setErro('Erro ao carregar detalhes do cupom.');
    }
  };

  const toggleCupom = async (row) => {
    const chave = `${row.LOJA}|${row.DATA}|${row.HORA}|${row.CUPOM}`;
    const aberto = !!expandedCupom[chave];
    setExpandedCupom((prev) => ({ ...prev, [chave]: !aberto }));
    if (!aberto) await carregarDetalhesCupom(row);
  };

  return (
    <DraggableWindow
      title="Cupons"
      icon="fa-receipt"
      onClose={onClose}
      onMinimize={onMinimize}
      isMinimized={isMinimized}
      zIndex={zIndex}
      onFocus={onFocus}
      initialWidth={1080}
      initialTop={90}
      initialLeft={180}
    >
      <form onSubmit={handleSubmit} className="consulta-form">
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 0.9fr 0.9fr 1fr 0.9fr auto', gap: '10px', alignItems: 'end' }}>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Cupom / Documento</label>
            <input value={cupom} onChange={(e) => setCupom(e.target.value)} placeholder="Ex: 123456" />
          </div>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Filial</label>
            <CustomSelect
              options={lojas.map((loja) => ({ value: loja.numero, label: `${loja.numero} - ${loja.nome}` }))}
              value={filial}
              onChange={setFilial}
              placeholder="Selecione a loja..."
              searchPlaceholder="Buscar loja..."
            />
          </div>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Data inicial</label>
            <input type="date" value={dataIni} onChange={(e) => setDataIni(e.target.value)} />
          </div>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Data final</label>
            <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>
          <div className="consulta-form-group" style={{ marginBottom: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div>
              <label>Valor mín.</label>
              <input value={valorMin} onChange={(e) => setValorMin(e.target.value)} placeholder="0,00" />
            </div>
            <div>
              <label>Valor máx.</label>
              <input value={valorMax} onChange={(e) => setValorMax(e.target.value)} placeholder="0,00" />
            </div>
          </div>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Forma pg.</label>
            <select value={formaPagamento} onChange={(e) => setFormaPagamento(e.target.value)}>
              <option value="">Todas</option>
              {formasPagamentoDisponiveis.map((forma) => (
                <option key={forma} value={forma}>{forma}</option>
              ))}
            </select>
          </div>
          <button type="submit" className="consulta-btn-submit" disabled={loading} style={{ height: '46px' }}>
            <i className="fas fa-search"></i> Buscar
          </button>
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
          Você pode buscar por cupom, por valor no período, ou apenas listar os cupons do período/filial.
        </div>
      </form>

      {erro && <div className="consulta-error" style={{ marginTop: 12 }}>{erro}</div>}
      {loading && <div className="consulta-loading" style={{ marginTop: 12 }}><i className="fas fa-spinner fa-spin"></i> Buscando cupons...</div>}

      {!loading && resultado.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {resumoFormaPagamento && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 10,
              padding: '9px 12px',
              border: '1px solid #bfdbfe',
              borderRadius: 10,
              background: '#eff6ff',
              color: '#1e3a8a',
              fontSize: 13
            }}>
              <span>
                Total recebido em <strong>{resumoFormaPagamento.forma}</strong>
                {' '}({resumoFormaPagamento.quantidadeCupons} cupom{resumoFormaPagamento.quantidadeCupons === 1 ? '' : 's'})
              </span>
              <strong style={{ fontSize: 15, whiteSpace: 'nowrap' }}>
                {money(resumoFormaPagamento.total)}
              </strong>
            </div>
          )}
          <div className="transfer-table-container">
            <table className="transfer-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Cupom</th>
                  <th>Filial</th>
                  <th>Data</th>
                  <th>Hora</th>
                  <th>Pagamento</th>
                  <th className="text-right">Itens</th>
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {resultado.map((row, idx) => {
                  const chave = `${row.LOJA}|${row.DATA}|${row.HORA}|${row.CUPOM}`;
                  const chaveCliente = `${row.LOJA}|${normalizeDateKey(row.DATA)}|${row.HORA || ''}|${row.CUPOM}`;
                  const chavePagamento = `${row.LOJA}|${normalizeDateKey(row.DATA)}|${row.CUPOM}`;
                  const aberto = !!expandedCupom[chave];
                  const produtos = produtosPorCupom[chave] || [];
                  const pagamentos = pagamentosPorCupom[chavePagamento] || [];
                  const cliente = clientesPorCupom[chaveCliente] || null;
                  return (
                    <React.Fragment key={`${row.CUPOM}-${idx}`}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            onClick={() => toggleCupom(row)}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#2563eb' }}
                            title={aberto ? 'Ocultar produtos' : 'Ver produtos'}
                          >
                            <i className={`fas fa-${aberto ? 'chevron-down' : 'chevron-right'}`}></i>
                          </button>
                        </td>
                        <td className="is-strong">{row.CUPOM}</td>
                        <td>{nomeLoja(row.LOJA)}</td>
                        <td>{formatDateBr(row.DATA)}</td>
                        <td>{row.HORA || '-'}</td>
                        <td style={{ minWidth: 220 }}>{pagamentos.length ? pagamentos.map(formatarPagamento).join(' | ') : '-'}</td>
                        <td className="text-right">{parseFloat(row.QTDITENS || 0).toFixed(0)}</td>
                        <td className="text-right">{money(row.VLRTOTAL)}</td>
                      </tr>
                      {aberto && (
                        <tr>
                          <td colSpan={8} style={{ background: '#f8fafc', padding: 0 }}>
                            <div style={{ padding: '10px 12px' }}>
                              {pagamentos.length > 0 && (
                                <div style={{ marginBottom: 10, fontSize: 13, color: '#334155' }}>
                                  <strong>Pagamentos:</strong> {pagamentos.map((item) => {
                                    const descricao = formatarPagamento(item);
                                    return `${descricao} (${money(item.VALOR)})`;
                                  }).join(' | ')}
                                </div>
                              )}
                              <table className="transfer-table" style={{ marginBottom: 0 }}>
                                <thead>
                                  <tr>
                                    <th>Cód.</th>
                                    <th>Barras</th>
                                    <th>Produto</th>
                                    <th className="text-right">Qtd</th>
                                    <th className="text-right">Vlr unit.</th>
                                    <th className="text-right">Vlr total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {produtos.length === 0 ? (
                                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '12px' }}>Nenhum produto encontrado.</td></tr>
                                  ) : produtos.map((item, prodIdx) => (
                                    <tr key={`${row.CUPOM}-${item.CDPRODU}-${prodIdx}`}>
                                      <td>{item.CDPRODU}</td>
                                      <td>{item.BARRA || item.BAR1 || item.BAR2 || '-'}</td>
                                      <td>{item.NOME || '-'}</td>
                                      <td className="text-right">{parseFloat(item.QTD || 0).toFixed(0)}</td>
                                      <td className="text-right">{money(item.VLRUNI)}</td>
                                      <td className="text-right">{money(item.VLRTOTAL)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <div style={{ marginTop: 10, fontSize: 13, color: '#334155' }}>
                                <strong>Cliente:</strong> {cliente?.NOME_CLIENTE || '-'}{' '}
                                <span style={{ marginLeft: 8 }}>
                                  <strong>CPF:</strong> {formatCpf(cliente?.CPF_CLIENTE)}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !erro && resultado.length === 0 && (
        <div style={{ marginTop: 16, color: '#6b7280', fontSize: 14 }}>
          Nenhum cupom encontrado com os filtros informados.
        </div>
      )}
    </DraggableWindow>
  );
};

export default WindowCupons;
